import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AsyncPipe, DecimalPipe, NgFor, NgIf } from '@angular/common';
import { Router } from '@angular/router';

import { CartItem } from '../../core/models/cart.model';
import { CartService } from '../../core/services/cart.service';
import { SessionService } from '../../core/services/session.service';
import { UiService } from '../../core/services/ui.service';
import { StorefrontService } from '../../core/services/storefront.service';
import { IconComponent } from './icon.component';
import { PriceComponent } from './price.component';
import { StepperComponent } from './stepper.component';
import { TranslatePipe } from '../pipes/translate.pipe';

/**
 * Live "Your order" panel docked beside the desktop menu: the server cart's
 * lines with quantity steppers, the bill, and the checkout entry (login-gated).
 */
@Component({
  selector: 'app-cart-panel',
  standalone: true,
  imports: [NgIf, NgFor, AsyncPipe, DecimalPipe, IconComponent, PriceComponent, StepperComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host { display: block; }
    aside { border: 1px solid var(--fr-border); border-radius: 18px; background: var(--fr-surface); overflow: hidden; box-shadow: var(--fr-shadow-sm); }
    .head { padding: 16px 18px; border-bottom: 1px solid var(--fr-border); display: flex; align-items: center; gap: 10px; }
    .head .title { font-weight: 750; font-size: 16px; color: var(--fr-text); flex: 1; }
    .badge { background: var(--brand-tint); color: var(--brand-deep); border-radius: 8px; padding: 3px 9px; font-size: 12.5px; font-weight: 800; }
    .empty { padding: 38px 22px; text-align: center; }
    .empty .ic { width: 54px; height: 54px; border-radius: 16px; background: var(--fr-surface-3); display: inline-flex; align-items: center; justify-content: center; }
    .empty .t { font-weight: 700; font-size: 15px; color: var(--fr-text); margin-top: 13px; }
    .empty .s { font-size: 13.5px; color: var(--fr-text-3); margin-top: 5px; line-height: 1.5; }
    .lines { max-height: 320px; overflow-y: auto; padding: 6px 0; scrollbar-width: thin; }
    .line { display: flex; gap: 11px; padding: 11px 16px; align-items: flex-start; }
    .line.off { opacity: .55; }
    .thumb { width: 48px; height: 48px; border-radius: 10px; flex-shrink: 0; overflow: hidden; background: var(--fr-surface-3); }
    .thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .ln { font-weight: 700; font-size: 14px; color: var(--fr-text); line-height: 1.3; }
    .ls { font-size: 12px; color: var(--fr-text-3); margin-top: 3px; line-height: 1.4; }
    .lr { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 8px; }
    .rm { border: none; background: none; cursor: pointer; color: var(--fr-text-3); padding: 2px; display: flex; }
    .bill { padding: 14px 18px 18px; border-top: 1px solid var(--fr-border); background: var(--fr-surface-2); }
    .hint { background: var(--brand-tint); border-radius: 10px; padding: 9px 12px; font-size: 12.5px; color: var(--brand-deep); font-weight: 650; margin-bottom: 12px; line-height: 1.45; }
    .row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; font-size: 13.5px; color: var(--fr-text-2); }
    .free { color: var(--fr-success-strong); font-weight: 750; font-size: 13px; }
    .total { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--fr-border); }
    .total span { font-weight: 750; font-size: 15px; color: var(--fr-text); }
    .cta { width: 100%; height: 50px; margin-top: 14px; border-radius: 13px; border: none; cursor: pointer; background: var(--brand); color: #fff;
      font-family: var(--fr-font); font-weight: 750; font-size: 16px; display: flex; align-items: center; justify-content: center; gap: 9px; box-shadow: 0 6px 16px -7px var(--brand-shadow); }
    .cta:disabled { opacity: .45; cursor: not-allowed; }
    .min { margin-top: 8px; font-size: 12px; color: var(--fr-danger); }
  `],
  template: `<aside *ngIf="cart.cart$ | async as c">
      <div class="head">
        <app-icon name="solar:bag-4-bold" [size]="19" color="var(--brand-deep)"></app-icon>
        <span class="title">{{ 'Your order' | t }}</span>
        <span *ngIf="cart.count > 0" class="badge fr-num">{{ cart.count }}</span>
      </div>

      <div *ngIf="!c.items.length" class="empty">
        <span class="ic"><app-icon name="solar:bag-smile-linear" [size]="26" color="var(--fr-text-3)"></app-icon></span>
        <div class="t">{{ 'Your cart is empty' | t }}</div>
        <div class="s">{{ 'Add something from the menu and it will show up here.' | t }}</div>
      </div>

      <ng-container *ngIf="c.items.length">
        <div class="lines">
          <div *ngFor="let l of c.items" class="line" [class.off]="!l.available">
            <span class="thumb"><img [src]="l.imageUrl || logo" alt="" onerror="this.style.visibility='hidden'"></span>
            <div style="flex:1;min-width:0">
              <div (click)="l.available && edit(l)" [style.cursor]="l.available ? 'pointer' : 'default'">
                <div class="ln">{{ l.name }}</div>
                <div class="ls" *ngIf="summary(l)">{{ summary(l) }}</div>
                <div class="ls" *ngIf="l.note" style="font-style:italic">“{{ l.note }}”</div>
              </div>
              <div class="ls" *ngIf="!l.available" style="color:var(--fr-danger);font-weight:700">{{ (l.unavailableReason || 'Unavailable') | t }}</div>
              <div class="lr">
                <div style="display:flex;align-items:center;gap:8px">
                  <app-stepper *ngIf="l.available" size="sm" [value]="l.quantity" [min]="1" [max]="20" (valueChange)="cart.setQuantity(l.id, $event)"></app-stepper>
                  <button *ngIf="l.available" class="rm" (click)="edit(l)" [attr.aria-label]="'Edit' | t"><app-icon name="solar:pen-linear" [size]="17"></app-icon></button>
                  <button class="rm" (click)="cart.removeLine(l.id)" [attr.aria-label]="'Remove' | t"><app-icon name="solar:trash-bin-trash-linear" [size]="17"></app-icon></button>
                </div>
                <app-price [value]="l.lineTotal" [size]="13.5" [weight]="750" [ccy]="c.bill.currencyCode"></app-price>
              </div>
            </div>
          </div>
        </div>

        <div class="bill">
          <div *ngIf="session.fulfillment === 'delivery' && c.freeDelivery.enabled && c.freeDelivery.remaining && !c.bill.freeDeliveryApplied" class="hint">
            {{ 'Add' | t }} <b class="fr-num">{{ c.freeDelivery.remaining | number }} {{ c.bill.currencyCode | t }}</b> {{ 'for free delivery' | t }}
          </div>
          <div class="row"><span>{{ 'Subtotal' | t }}</span><app-price [value]="c.bill.subtotal" [size]="13.5" [weight]="650" [ccy]="c.bill.currencyCode"></app-price></div>
          <div class="row" *ngIf="c.bill.discount"><span class="free">{{ 'Offer discount' | t }}<span *ngIf="c.promoCode" class="fr-num" style="color:var(--fr-text-3);font-weight:600"> · {{ c.promoCode }}</span></span><span class="fr-num free">{{ c.bill.discount | number }} {{ c.bill.currencyCode | t }}</span></div>
          <div class="row" *ngIf="session.fulfillment === 'delivery'">
            <span>{{ 'Delivery fee' | t }}</span>
            <span *ngIf="c.bill.freeDeliveryApplied; else fee" class="free">{{ 'FREE' | t }}</span>
            <ng-template #fee><app-price [value]="c.bill.deliveryFee || 0" [size]="13.5" [weight]="650" [ccy]="c.bill.currencyCode"></app-price></ng-template>
          </div>
          <div class="row" *ngIf="c.bill.serviceCharge > 0"><span>{{ 'Service charge' | t }}</span><app-price [value]="c.bill.serviceCharge" [size]="13.5" [weight]="650" [ccy]="c.bill.currencyCode"></app-price></div>
          <div class="row" *ngIf="c.bill.vatAmount"><span>{{ 'VAT' | t }}</span><app-price [value]="c.bill.vatAmount" [size]="13.5" [weight]="650" [ccy]="c.bill.currencyCode"></app-price></div>
          <div class="total"><span>{{ 'Total' | t }}</span><app-price [value]="c.bill.total" [size]="19" [weight]="800" [ccy]="c.bill.currencyCode"></app-price></div>
          <div *ngIf="!c.minOrder.met && c.minOrder.amount" class="min">{{ 'Minimum order' | t }} {{ c.minOrder.amount | number }} {{ c.bill.currencyCode | t }}</div>
          <button class="cta" [disabled]="!cart.count || !c.minOrder.met" (click)="checkout()">
            {{ 'Checkout' | t }} <app-icon name="solar:arrow-right-linear" [size]="18"></app-icon>
          </button>
        </div>
      </ng-container>
    </aside>`,
})
export class CartPanelComponent {
  constructor(
    public cart: CartService,
    public session: SessionService,
    private ui: UiService,
    private sf: StorefrontService,
    private router: Router,
  ) {}

  /** Stand-in for items with no photo: the default item image, else the logo. */
  get logo(): string { return this.sf.brand?.itemImage || this.sf.brand?.logo || ''; }
  edit(l: CartItem): void { this.ui.editCartLine(l, this.logo); }
  summary(l: CartItem): string { return [l.variantName, ...l.options.map((o) => o.name)].filter(Boolean).join(' · '); }

  checkout(): void {
    if (!this.session.loggedIn) { this.ui.requireLogin('Sign in to check out.'); return; }
    this.router.navigate(['/checkout']);
  }
}
